import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ContactRelationshipsService, ContactsService } from '../_services/';
import { AppState } from '../../reducers';
import {
  ClearContactRelationshipErrors,
  ContactRelationshipActionToggleLoading,
  ContactRelationshipActionTypes,
  ContactRelationshipCreated,
  ContactRelationshipOnServerCreated,
  ContactRelationshipsPageCancelled,
  ContactRelationshipsPageLoaded,
  ContactRelationshipsPageRequested,
  ContactRelationshipsPageToggleLoading,
  ContactRelationshipUpdated,
  OneContactRelationshipDeleted,
  OneContactRelationshipOnServerDeleted,
  ContactRelationshipOnServerUpdated,
  ThrowContactRelationshipError,
} from '../_actions/contact-relationship.actions';

@Injectable()
export class ContactRelationshipEffects {
  showPageLoadingDistpatcher = new ContactRelationshipsPageToggleLoading({ isLoading: true });
  hideLoadingDistpatcher = new ContactRelationshipsPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new ContactRelationshipActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new ContactRelationshipActionToggleLoading({ isLoading: false });

  @Effect()
  loadContactRelationshipsPage$ = this.actions$.pipe(
    ofType<ContactRelationshipsPageRequested>(
      ContactRelationshipActionTypes.ContactRelationshipsPageRequested,
    ),
    mergeMap(({ payload }) => {
      if (payload.fromwhere && payload.fromwhere === 'Cont') {
        return this.contactRelationshipsService
          .findContactRelationships(payload.contactId, payload.type, false, payload?.page)
          .pipe(
            catchError((error) => {
              return of({ error });
            }),
            map((res) => {
              if (res.error) {
                this.store.dispatch(new ThrowContactRelationshipError({ error: res }));
                return new ContactRelationshipsPageCancelled();
              } else {
                this.store.dispatch(new ClearContactRelationshipErrors());
                return new ContactRelationshipsPageLoaded({
                  contactRelationships: res.data,
                  totalCount: res.data.length,
                });
              }
            }),
          );
      } else {
        return this.contactRelationshipsService.findDoctorRelationships(payload.contactId).pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res.error) {
              this.store.dispatch(new ThrowContactRelationshipError({ error: res }));
              return new ContactRelationshipsPageCancelled();
            } else {
              this.store.dispatch(new ClearContactRelationshipErrors());
              return new ContactRelationshipsPageLoaded({
                contactRelationships: res.data,
                totalCount: res.data.length,
              });
            }
          }),
        );
      }
    }),
  );

  @Effect()
  deleteContactRelationship$ = this.actions$.pipe(
    ofType<OneContactRelationshipOnServerDeleted>(
      ContactRelationshipActionTypes.OneContactRelationshipOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.contactRelationshipsService
        .deleteContactRelationship(payload.contactRelationship)
        .pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res && res.error) {
              return new ThrowContactRelationshipError({ error: res });
            } else {
              this.store.dispatch(new ClearContactRelationshipErrors());
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              return new OneContactRelationshipDeleted(payload);
            }
          }),
        );
    }),
  );

  @Effect()
  updateContactRelationship$ = this.actions$.pipe(
    ofType<ContactRelationshipOnServerUpdated>(
      ContactRelationshipActionTypes.ContactRelationshipOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.contactRelationshipsService
        .updateContactRelationship(payload.contactRelationship, payload.agreement)
        .pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res.error) {
              return new ThrowContactRelationshipError({ error: res });
            } else {
              this.store.dispatch(new ClearContactRelationshipErrors());
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              this.store.dispatch(this.hideLoadingDistpatcher);
              return new ContactRelationshipUpdated(payload);
            }
          }),
        );
    }),
  );

  @Effect()
  createContactRelationship$ = this.actions$.pipe(
    ofType<ContactRelationshipOnServerCreated>(
      ContactRelationshipActionTypes.ContactRelationshipOnServerCreated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      if (payload.contact_id) {
        return this.contactRelationshipsService
          .createEntityRelationship(payload.contactRelationship, payload.contact_id)
          .pipe(
            catchError((error) => {
              return of(error);
            }),
            map((res) => {
              if (res.error) {
                return new ThrowContactRelationshipError({ error: res });
              } else {
                this.store.dispatch(new ClearContactRelationshipErrors());
                this.store.dispatch(this.hideActionLoadingDistpatcher);
                this.store.dispatch(this.hideLoadingDistpatcher);
                return new ContactRelationshipCreated({ contactRelationship: res.data });
              }
            }),
          );
      } else {
        return this.contactRelationshipsService
          .createContactRelationship(
            payload.contactRelationship,
            payload.contact,
            payload.agreement,
          )
          .pipe(
            catchError((error) => {
              return of(error);
            }),
            map((res) => {
              if (res.error) {
                return new ThrowContactRelationshipError({ error: res });
              } else {
                this.store.dispatch(new ClearContactRelationshipErrors());
                this.store.dispatch(this.hideActionLoadingDistpatcher);
                this.store.dispatch(this.hideLoadingDistpatcher);

                return new ContactRelationshipCreated({ contactRelationship: res.data });
              }
            }),
          );
      }
    }),
  );

  constructor(
    private actions$: Actions,
    private contactRelationshipsService: ContactRelationshipsService,
    private contactsService: ContactsService,
    private store: Store<AppState>,
  ) {}
}
